import {
  BarChart3,
  Gift,
  LifeBuoy,
  MessageCircleQuestion,
  Radar,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Store,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { ROUTES } from "@/routes";

/**
 * One navigable destination.
 *
 * `requiresGroup` is the single place a destination's visibility is declared,
 * so the sidebar and the command palette gate identically rather than each
 * keeping their own list.
 */
export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  requiresGroup?: string;
  /** Matches the route exactly rather than by prefix. */
  end?: boolean;
}

export interface NavSection {
  /** Heading shown above the group, or null for the unlabelled first group. */
  label: string | null;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    label: null,
    items: [
      { label: "Dashboard", to: ROUTES.SIGNALS, icon: Radar, end: true },
      { label: "Merchants", to: ROUTES.MERCHANTS, icon: Store },
      { label: "Support", to: ROUTES.SUPPORT, icon: LifeBuoy },
      {
        label: "Assistant gaps",
        to: ROUTES.ASSISTANT_GAPS,
        icon: MessageCircleQuestion,
      },
      { label: "Events", to: ROUTES.EVENTS, icon: Zap },
      {
        label: "Tools",
        to: ROUTES.TOOLS,
        icon: Wrench,
        requiresGroup: "CPOS-Developers",
      },
    ],
  },
  {
    label: "Marketing",
    items: [
      { label: "Referrals", to: ROUTES.MARKETING_REFERRALS, icon: Gift },
      {
        label: "Activation Bonus",
        to: ROUTES.MARKETING_ACTIVATION_BONUS,
        icon: Sparkles,
      },
    ],
  },
  {
    label: "Terminal",
    items: [
      { label: "Analytics", to: ROUTES.TERMINAL_ANALYTICS, icon: BarChart3 },
      {
        label: "Terminal Accounts",
        to: ROUTES.TERMINAL_ACCOUNTS,
        icon: Smartphone,
      },
    ],
  },
];

export const FOOTER_NAV_ITEMS: NavItem[] = [
  { label: "Admins", to: ROUTES.ADMINS, icon: ShieldCheck },
];

/**
 * Every destination in one flat list, for consumers that do not render
 * sections — principally the command palette.
 */
export function allNavItems(): NavItem[] {
  return [
    ...NAV_SECTIONS.flatMap((section) => section.items),
    ...FOOTER_NAV_ITEMS,
  ];
}

/** Narrows a list to what a user in `groups` is allowed to see. */
export function visibleNavItems(
  items: NavItem[],
  groups: readonly string[],
): NavItem[] {
  return items.filter(
    (item) => !item.requiresGroup || groups.includes(item.requiresGroup),
  );
}
